import { useQuery, useSubscription } from '@apollo/client';
import { useEffect, useState } from 'react';
import { LIST_USERS, ON_PRESENCE_UPDATE } from '../graphql/operations';

const KNOWN_AGENTS = [
  { username: 'sherlock', displayName: 'SHERLOCK HOLMES', isAgent: true }
];

export function mapServerUser(user) {
  return {
    username: user.username,
    email: user.email,
    displayName: user.displayName || user.username,
    roles: user.roles,
    permissions: user.permissions,
    authProvider: user.authProvider,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    lastLogin: user.lastLogin,
    lastSeenAt: user.lastSeenAt || null,
    presenceOverride: user.presenceOverride || null,
    statusMessage: user.statusMessage || null,
    isAgent: false
  };
}

export function mergePresenceUpdate(users = [], profile) {
  if (!profile?.username) return users;

  let matched = false;
  const merged = users.map((user) => {
    if (user.username !== profile.username) return user;
    matched = true;
    return {
      ...user,
      lastSeenAt: profile.lastSeenAt || null,
      presenceOverride: profile.presenceOverride || null,
      statusMessage: profile.statusMessage || null
    };
  });

  if (matched) return merged;

  return [
    ...merged,
    mapServerUser({
      username: profile.username,
      displayName: profile.username,
      lastSeenAt: profile.lastSeenAt,
      presenceOverride: profile.presenceOverride,
      statusMessage: profile.statusMessage
    })
  ];
}

export function useUsers() {
  const { data, loading } = useQuery(LIST_USERS, {
    fetchPolicy: 'cache-and-network'
  });
  const [, setPresenceTick] = useState(() => Date.now());

  useSubscription(ON_PRESENCE_UPDATE, {
    onData: ({ client, data: subscriptionResult }) => {
      const profile = subscriptionResult?.data?.onPresenceUpdate;
      if (!profile?.username) return;

      try {
        const existing = client.readQuery({ query: LIST_USERS });
        if (!existing?.listUsers) return;

        client.writeQuery({
          query: LIST_USERS,
          data: {
            listUsers: mergePresenceUpdate(existing.listUsers, profile)
          }
        });
      } catch (_error) {
        // If the users query is not in cache yet, the next network response will supply it.
      }
    }
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      setPresenceTick(Date.now());
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const serverUsers = (data?.listUsers || []).map(mapServerUser);
  const users = [...serverUsers, ...KNOWN_AGENTS];

  // Build a lookup map by username for quick access
  const userMap = {};
  users.forEach((u) => {
    userMap[u.username] = u;
  });

  return { users, userMap, loading };
}
